import React, { useState } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import Icon from 'components/DesignSystem/Icon';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import months from 'helper/Shared/months';
import { cssVar } from 'constants/theme';

import './index.scss';

type OpenField = 'month' | 'year' | '';

// `months.long` é indexado de 1 a 12; a view de Vobs trabalha com mês 0-indexado
// (converte para 1-indexado só no payload da API), então achatamos para um array.
const MONTH_LABELS = Object.values(months.long);

export interface MonthYearPickerProps {
  month: number | null;
  year: number;
  years: number[];
  enabledMonths: Set<number>;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  contextLabel: string;
}

export default function MonthYearPicker({
  month,
  year,
  years,
  enabledMonths,
  onMonthChange,
  onYearChange,
  contextLabel,
}: MonthYearPickerProps) {
  const [openField, setOpenField] = useState<OpenField>('');

  const availableMonths = MONTH_LABELS
    .map((label, index) => ({ label, index }))
    .filter(({ index }) => enabledMonths.has(index));

  return (
    <div className="vobs-month-year-picker">
      <button
        type="button"
        className={`vobs-month-year-field${month === null ? ' placeholder' : ''}`}
        aria-haspopup="dialog"
        aria-label={`Selecionar mês — ${contextLabel}`}
        onClick={() => setOpenField('month')}
      >
        <span>{month !== null ? MONTH_LABELS[month] : 'Mês'}</span>
        <Icon iconType="down_arrow" color={cssVar('text-muted')} size="small" />
      </button>

      <button
        type="button"
        className="vobs-month-year-field"
        aria-haspopup="dialog"
        aria-label={`Selecionar ano — ${contextLabel}`}
        onClick={() => setOpenField('year')}
      >
        <span>{year}</span>
        <Icon iconType="down_arrow" color={cssVar('text-muted')} size="small" />
      </button>

      <BottomSheet open={openField === 'month'} dismiss={() => setOpenField('')} title="Mês">
        {availableMonths.length === 0 && (
          <Snackbar className="mt-2">Nenhum mês disponível para este ano.</Snackbar>
        )}
        {availableMonths.length > 0 && (
          <p className="vobs-month-year-hint">
            {`Só aparecem os meses de ${year} já fechados no acompanhamento hidrológico.`}
          </p>
        )}
        {availableMonths.map(({ label, index }) => (
          <OptionRow
            key={label}
            label={label}
            selected={index === month}
            onSelect={() => {
              onMonthChange(index);
              setOpenField('');
            }}
          />
        ))}
      </BottomSheet>

      <BottomSheet open={openField === 'year'} dismiss={() => setOpenField('')} title="Ano">
        {years.map((option) => (
          <OptionRow
            key={option}
            label={String(option)}
            selected={option === year}
            onSelect={() => {
              onYearChange(option);
              setOpenField('');
            }}
          />
        ))}
      </BottomSheet>
    </div>
  );
}
